import { Card, Descriptions, Divider, Spin, Typography } from 'antd';
import { useReportProjectWorkItemsDisplay } from '@/hooks/useReportProjectWorkItemsDisplay';
import type { ReportRecord } from '@/types/report';

export interface ReportProjectWorkItemsDisplayProps {
  report: ReportRecord | null;
  /** 移动详情页样式 */
  isMobile?: boolean;
}

function displayText(value: string | undefined | null): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

function MobileProjectWorkItemCard({
  projectName,
  progressPercent,
  progressContent,
  problemContent,
  nextPlanContent,
}: {
  projectName: string;
  progressPercent: number;
  progressContent: string;
  problemContent?: string;
  nextPlanContent?: string;
}) {
  return (
    <div className="report-project-work-item report-project-work-item--readonly">
      <div className="report-project-work-item__header">
        <span className="report-project-work-item__title">{projectName}</span>
        <span className="report-project-work-item__percent">汇报进度 {progressPercent}%</span>
      </div>
      <div className="mobile-detail-row">
        <span className="mobile-detail-label">项目进展</span>
        <span className="mobile-detail-value mobile-detail-value--multiline">{progressContent}</span>
      </div>
      <div className="mobile-detail-row">
        <span className="mobile-detail-label">项目问题</span>
        <span className="mobile-detail-value mobile-detail-value--multiline">
          {displayText(problemContent)}
        </span>
      </div>
      <div className="mobile-detail-row">
        <span className="mobile-detail-label">下一步计划</span>
        <span className="mobile-detail-value mobile-detail-value--multiline">
          {displayText(nextPlanContent)}
        </span>
      </div>
    </div>
  );
}

function DesktopProjectWorkItemCard({
  projectName,
  progressPercent,
  progressContent,
  problemContent,
  nextPlanContent,
}: {
  projectName: string;
  progressPercent: number;
  progressContent: string;
  problemContent?: string;
  nextPlanContent?: string;
}) {
  return (
    <Card size="small" title={`${projectName} · 汇报进度 ${progressPercent}%`}>
      <Descriptions column={1} size="small">
        <Descriptions.Item label="项目进展">
          <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
            {progressContent}
          </Typography.Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label="项目问题">
          <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
            {displayText(problemContent)}
          </Typography.Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label="下一步计划">
          <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
            {displayText(nextPlanContent)}
          </Typography.Paragraph>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
}

export function ReportProjectWorkItemsDisplay({
  report,
  isMobile = false,
}: ReportProjectWorkItemsDisplayProps) {
  const { items, loading } = useReportProjectWorkItemsDisplay(report);

  if (!report) {
    return null;
  }

  if (loading && items.length === 0) {
    return isMobile ? (
      <section className="mobile-section" style={{ textAlign: 'center', padding: 16 }}>
        <Spin size="small" />
      </section>
    ) : (
      <div style={{ padding: '8px 0' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  if (isMobile) {
    return (
      <section className="mobile-section">
        <div className="mobile-section__title" style={{ marginBottom: 12 }}>
          项目进展
        </div>
        <div className="report-project-work-items-section__list">
          {items.map((item) => (
            <MobileProjectWorkItemCard
              key={item.projectReportId ?? item.clientKey}
              projectName={item.projectName}
              progressPercent={item.progressPercent}
              progressContent={item.progressContent}
              problemContent={item.problemContent}
              nextPlanContent={item.nextPlanContent}
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <>
      <Divider orientation="left" plain>
        项目进展（{items.length}）
      </Divider>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((item) => (
          <DesktopProjectWorkItemCard
            key={item.projectReportId ?? item.clientKey}
            projectName={item.projectName}
            progressPercent={item.progressPercent}
            progressContent={item.progressContent}
            problemContent={item.problemContent}
            nextPlanContent={item.nextPlanContent}
          />
        ))}
      </div>
    </>
  );
}
